import React, { useState } from 'react';
import { Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';

import { MasterConfigs } from '../../../../../../interfaces/entity';
import ReusableTable from '../../../../../../components/TableComponent';

interface ProjectRecord {
  _id: string;
  projectName: string;
  assignedDate: string;
  client: string;
  engagementTime: string;
  developersDeployed: number;
  totalDealValue: number;
  revenueCost: number;
  profit: number;
  feedback: string;
  stage: 'Completed' | 'In Progress';
}

const ProjectOverviewTable: React.FC = () => {
  const [search, setSearch] = useState('');

  // Stats Card Data from Figma
  const stats = [
    { label: 'Total Projects Completed', count: 12, color: 'border-teal-400' },
    { label: 'Ongoing Projects', count: 2, color: 'border-orange-400' },
    { label: 'On Hold Projects', count: 6, color: 'border-green-500' },
    { label: 'Cancelled Projects', count: 4, color: 'border-red-500' },
  ];

  const tableConfig: MasterConfigs = {
    api: () => '/projects-handled',
    loadPagination: true,
    queryParams: { search },

    renderComponent: () => (
      <div className="flex justify-between items-center ">
        <h3 className="text-base font-semibold text-gray-800">Projects Handled</h3>
        <Input
          size="middle"
          placeholder="Search"
          prefix={<SearchOutlined className="text-gray-400" />}
          className="w-64 rounded-md border-gray-300"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
    ),

    getColumns: (): ColumnsType<ProjectRecord> => [
      {
        title: 'Project Name',
        dataIndex: 'projectName',
        render: (text) => <span className="text-gray-600 font-normal whitespace-nowrap">{text || 'Project Name'}</span>,
      },
      {
        title: 'Assigned Date',
        dataIndex: 'assignedDate',
        render: (date) => <span className="whitespace-nowrap text-gray-600">{date}</span>,
      },
      {
        title: 'Client',
        dataIndex: 'client',
        render: (text) => <span className="whitespace-nowrap text-gray-600">{text}</span>,
      },
      {
        title: 'Engagement Time',
        dataIndex: 'engagementTime',
        align: 'center',
        render: (text) => <span className="text-gray-600">{text}</span>,
      },
      {
        title: 'Developers Deployed',
        dataIndex: 'developersDeployed',
        align: 'center',
        render: (val) => <span className="text-gray-600">{val}</span>,
      },
      {
        title: 'Total Deal Value',
        dataIndex: 'totalDealValue',
        render: (v) => <span className="whitespace-nowrap text-gray-600">{v?.toLocaleString()} ₹</span>,
      },
      {
        title: 'Revenue Cost',
        dataIndex: 'revenueCost',
        render: (v) => <span className="whitespace-nowrap text-gray-600">{v?.toLocaleString()} ₹</span>,
      },
      {
        title: 'Profit',
        dataIndex: 'profit',
        render: (v) => <span className="whitespace-nowrap text-gray-600 font-medium">{v?.toLocaleString()} ₹</span>,
      },
      {
        title: 'Feedback',
        dataIndex: 'feedback',
        width: 150,
        render: (text) => (
          <div className="truncate text-gray-500 text-xs w-32" title={text}>
            {text || 'Lorem Ipsum Lor...'}
          </div>
        ),
      },
      {
        title: 'Stage',
        dataIndex: 'stage',
        render: (stage) => (
          <span className={stage === 'Completed' ? 'text-gray-700' : 'text-blue-500 font-medium'}>
            {stage}
          </span>
        ),
      },
      {
        title: 'Timeline',
        align: 'center',
        render: () => (
          <span className="text-blue-500 cursor-pointer hover:underline text-sm font-medium">
            View
          </span>
        ),
      },
    ],

    tableProps: {
      bordered: false,
      size: 'middle',
      scroll: { x: 'max-content' },
      pagination: {
        size: 'small',
        showSizeChanger: false,
        className: 'mt-6',
      }
    },
  };

  return (
    <div >
      {/* Project Overview Stats Section */}
      <div >
        <h2 className="text-lg font-semibold text-gray-800 ">Project Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {stats.map((item, idx) => (
            <div 
              key={idx} 
              className={`bg-white p-4 rounded-md shadow-sm border-l-4 ${item.color} flex flex-col justify-center h-24`}
            >
              <div className="text-2xl font-bold text-gray-800">{item.count}</div>
              <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Table Section */}
      <div className="bg-white p-6 rounded-md shadow-sm border border-gray-100 mt-6 pb-[50px]">
        <ReusableTable<ProjectRecord> config={tableConfig} />
      </div>
    </div>
  );
};

export default ProjectOverviewTable;